/* DVW Source a Watch form.

   One page, two entry paths. A shopper who taps "Request a Restock" on a sold listing arrives
   with ?ref=<handle> and a context object the snippet wrote to sessionStorage, so the piece they
   are asking after is already on screen and the flow is short. A shopper who navigates here
   directly gets the longer flow that describes a piece from scratch.

   The submitted payload uses the Cloudflare Worker's existing "Request a Watch" form_type, which
   is what routes the lead to the buyer list and the 📬 Telegram format. request_watch_url and
   request_notes keep the meaning the leads dashboard already expects; the structured answers ride
   alongside as their own keys.

   Option sets are not authored here. Brand, model, metal, case size, bracelet, shape and budget
   all come from snippets/dvw-request-data.liquid, generated off the live catalogue, so the form
   cannot offer a combination the business has never handled. Movement and complications have no
   catalogue data behind them, so COMPLICATIONS below is the one hand-kept constraint. */
(function () {
  'use strict';

  /* The exact string the Cloudflare Worker routes on; do not reword. */
  var REQUEST = 'Request a Watch';
  var STORE_KEY = 'dvw-source-watch/v1';
  /* Written by snippets/restock.liquid, which runs in product scope and therefore knows the
     piece's metafields. Read once, then owned by this form's own saved state. */
  var REF_KEY = 'dvw-source-watch/ref';
  var KEY_LETTERS = 'ABCDEFGH';

  var MODE_RESTOCK = 'Restock';
  var MODE_OPEN = 'Open inquiry';

  /* Sentinel option values. Compared against stored answers, so changing one is a data change. */
  var ANY_BRAND = 'Open to anything';
  var OTHER_BRAND = 'Another brand';
  var SOMETHING_ELSE = 'Something else';
  var JUST_TIME = 'Just time, nothing extra';
  var NOTHING_FIXED = 'Nothing is fixed';
  var OWN_NUMBER = 'Tell us a number';
  var REF_YES = 'Yes, this one';
  var REF_CLOSE = 'Close, but not exact';
  var REF_NO = 'Something else entirely';
  var FLEX_EXACT = 'This exact reference';

  /* How many brand chips before the list stops being scannable. The rest are reachable through
     "Another brand", which opens a write-in. */
  var BRAND_CHIPS = 10;

  var ICON_NEXT =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>';
  var ICON_BACK =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 12H5M11 18l-6-6 6-6"/></svg>';
  var ICON_CHECK =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';

  /* The one constraint the catalogue cannot supply. custom.movement is unpopulated across all
     4,300 archived watches and complications are not tagged, so which complications a movement
     can plausibly carry is kept here by hand. The point is not pedantry: offering a quartz
     skeleton invites a request nobody can fill. */
  var COMPLICATIONS = [
    { v: 'Date', on: ['Automatic', 'Manual wind', 'Quartz'] },
    { v: 'Day & date', on: ['Automatic', 'Quartz'] },
    { v: 'Chronograph', on: ['Automatic', 'Manual wind', 'Quartz'] },
    { v: 'GMT / dual time', on: ['Automatic', 'Quartz'] },
    { v: 'Moonphase', on: ['Automatic', 'Manual wind'] },
    { v: 'Power reserve', on: ['Automatic', 'Manual wind'] },
    { v: 'Small seconds', on: ['Automatic', 'Manual wind'] },
    { v: 'Skeletonised / openworked', on: ['Automatic', 'Manual wind'] }
  ];

  var LOVED = [
    'The size on the wrist',
    'The dial',
    'The case shape',
    'The metal',
    'The bracelet',
    'The model itself',
    'The era it is from',
    'The price'
  ];

  var SOURCING_LINE =
    'Every piece we handle is vintage and individual, so condition, dial and exact layout vary ' +
    'from one example to the next.';

  /* Some catalogue values are internal shorthand that would not read well on a customer-facing
     button. "Special" in particular is the archive's catch-all for vermeil, plated cases and
     unusual finishes, so it needs saying out loud. Only the label changes; the value sent to the
     backend stays the catalogue's own string so a request still matches product data. */
  var VALUE_LABELS = {
    Special: { l: 'Something unusual', hint: 'Vermeil, plated or an unusual finish' },
    '< 30mm': { l: 'Under 30mm' },
    '30 - 35mm': { l: '30 – 35mm' },
    '35 - 40mm': { l: '35 – 40mm' },
    '40mm +': { l: '40mm and up' }
  };

  function node(html) {
    var tpl = document.createElement('template');
    tpl.innerHTML = html.trim();
    return tpl.content.firstElementChild;
  }

  /* The label a customer should see for a bare catalogue value, for the places that show a value
     as supporting text rather than as the button itself. */
  function prettyValue(v) {
    var pretty = VALUE_LABELS[v];
    return pretty ? pretty.l : v;
  }

  function option(raw) {
    var o =
      raw && typeof raw === 'object'
        ? { v: raw.v, l: raw.l || raw.v, hint: raw.hint || '' }
        : { v: raw, l: raw, hint: '' };
    var pretty = VALUE_LABELS[o.v];
    /* Only rewrite a bare catalogue value; a caller that supplied its own label meant it. */
    if (pretty && o.l === o.v) {
      o.l = pretty.l;
      if (!o.hint) o.hint = pretty.hint || '';
    }
    return o;
  }

  function asList(v) {
    if (Array.isArray(v)) return v.slice();
    if (v === undefined || v === null || v === '') return [];
    return [String(v)];
  }

  function boot(root) {
    if (!root || root.hasAttribute('data-ready')) return;
    root.setAttribute('data-ready', '1');

    function readJson(selector, fallback) {
      var el = root.querySelector(selector);
      if (!el) return fallback;
      try {
        return JSON.parse(el.textContent) || fallback;
      } catch (err) {
        return fallback;
      }
    }

    var CFG = readJson('script[data-config]', {});
    var MANIFEST = readJson('script[data-dvw-request-manifest]', {});
    var BRANDS = MANIFEST.brands || [];
    var BUDGET_LABELS = MANIFEST.budget_labels || {};
    var BRAND_SLUGS = MANIFEST.brand_slugs || {};
    var ALL = MANIFEST.all || {};

    var SUBMIT_URL = CFG.submitUrl || '';
    var PREVIEW = !!CFG.designMode || root.hasAttribute('data-design-mode') || !SUBMIT_URL;
    var PHONE = CFG.phone || '';
    var SHOP_ALL = CFG.shopAllUrl || '/collections/all';
    var HOME = CFG.homeUrl || '/';
    var CURRENCY = CFG.currency || 'USD';
    var POLICY_URL = CFG.policyUrl || '/pages/any-questions';

    var stage = root.querySelector('[data-stage]');
    var progressFill = root.querySelector('[data-progress-fill]');
    if (!stage) return;

    /* -------------------------------------------------------------- state */

    var answers = {};
    var order = [];
    var pos = 0;
    var partialSent = false;
    var submitting = false;
    var introSkipped = false;
    var historyReady = false;
    /* The id of the furthest question drawn this session, so a reload resumes where the customer
       was rather than wherever validation happens to stop. Bookkeeping, not an answer, so it is
       saved beside the answers instead of among them. */
    var reached = '';
    /* The piece a restock request is about: title, url, image, price and whatever metafields the
       snippet could see. Null for an open inquiry. */
    var piece = null;

    /* -------------------------------------------------------------- manifest lookups */

    function brandEntry(label) {
      for (var i = 0; i < BRANDS.length; i++) {
        if (BRANDS[i].l === label) return BRANDS[i];
      }
      return null;
    }

    /* The brand the request is about, whichever path got us here. */
    function activeBrand() {
      if (keepingPiece()) return piece.brand || '';
      return answers.brand === ANY_BRAND || answers.brand === OTHER_BRAND ? '' : answers.brand || '';
    }

    /* Options scoped to the chosen brand, falling back to the union across the archive when no
       brand is known yet. Named brands we hold four or more of always have their own lists. */
    function scoped(key) {
      var entry = brandEntry(activeBrand());
      var list = entry ? entry[key] : null;
      if (!list || !list.length) list = ALL[key] || [];
      return list.slice();
    }

    function brandOptions() {
      var out = [];
      for (var i = 0; i < BRANDS.length && out.length < BRAND_CHIPS; i++) {
        /* "Other" is a real custom.brand value covering everything unlisted, which is exactly
           what the write-in below is for; offering both would read as a duplicate. */
        if (BRANDS[i].l === 'Other') continue;
        out.push({ v: BRANDS[i].l, l: BRANDS[i].l });
      }
      out.push({ v: OTHER_BRAND, l: OTHER_BRAND });
      out.push({ v: ANY_BRAND, l: ANY_BRAND, hint: 'Show me what you would pick' });
      return out;
    }

    /* Small brands and the "another brand" write-in have no model list in the catalogue, in which
       case the write-in is the only way through and should say so rather than reading as an
       afterthought called "Something else". */
    function modelOptions() {
      var entry = brandEntry(activeBrand());
      var models = entry ? entry.models : [];
      var out = models.map(function (m) {
        return { v: m, l: m };
      });
      out.push({ v: SOMETHING_ELSE, l: models.length ? SOMETHING_ELSE : 'Tell us the model' });
      return out;
    }

    function complicationOptions() {
      var movement = answers.movement || '';
      var out = COMPLICATIONS.filter(function (c) {
        return !movement || movement === 'No preference' || c.on.indexOf(movement) > -1;
      }).map(function (c) {
        return { v: c.v, l: c.v };
      });
      out.push({ v: JUST_TIME, l: JUST_TIME });
      return out;
    }

    function budgetOptions() {
      var entry = brandEntry(activeBrand());
      var keys = entry && entry.budgets.length ? entry.budgets : ALL.budgets || [];
      var thin = (entry && entry.thin) || [];
      var out = keys.map(function (k) {
        return {
          v: BUDGET_LABELS[k] || k,
          l: BUDGET_LABELS[k] || k,
          hint: thin.indexOf(k) > -1 ? 'We see few at this level' : ''
        };
      });
      out.push({ v: OWN_NUMBER, l: OWN_NUMBER });
      return out;
    }

    /* What a restock request can ask to be held constant, built from the attributes the snippet
       actually managed to read off the product. Old listings carry fewer metafields than new
       ones, so this list is as long as the data allows and no longer. */
    function mustMatchOptions() {
      if (!piece) return [];
      var out = [];
      /* The value carries the catalogue's own string so the request still matches product data;
         the hint is what the customer reads, so it takes the customer-facing wording. */
      function add(value, label) {
        if (value) out.push({ v: label + ': ' + value, l: label, hint: prettyValue(value) });
      }
      add(piece.model, 'Same model');
      add(piece.material, 'Same metal');
      add(piece.case_size, 'Same case size');
      add(piece.bracelet, 'Same bracelet');
      add(piece.shape_label, 'Same case shape');
      out.push({ v: 'Same dial look', l: 'Same dial look' });
      return out;
    }

    /* The non-negotiables step offers the customer's own picks back to them, so it cannot list
       anything they did not already choose. */
    function pickedOptions() {
      var out = [];
      function add(values) {
        asList(values).forEach(function (v) {
          if (v && v !== JUST_TIME && out.indexOf(v) < 0) out.push(v);
        });
      }
      add(answers.model === SOMETHING_ELSE ? answers.model_note : answers.model);
      add(answers.reference);
      add(answers.complications);
      add(answers.material);
      add(answers.case_size);
      var opts = out.map(function (v) {
        return { v: v, l: v };
      });
      opts.push({ v: NOTHING_FIXED, l: NOTHING_FIXED });
      return opts;
    }

    /* -------------------------------------------------------------- questions */

    /* Which questions are on offer is derived from the reference piece and the answers about it,
       never from a mutable mode flag. "Something else entirely" has to leave reference_piece
       itself on the list, because dropping the question the customer has their finger on would
       shift every index behind it and send them to the wrong next screen. */

    function hasPiece() {
      return !!piece;
    }

    /* Describing a piece from scratch: either nothing was handed over, or the customer looked at
       what was handed over and said it is not what they want. */
    function isOpen() {
      if (!hasPiece()) return true;
      return answers.reference_piece === REF_NO;
    }

    /* The exact complement of isOpen. True before reference_piece has been answered, which is
       what lets the request read as a restock from the first screen and keeps the step count
       steady instead of growing by one the moment the piece is confirmed. */
    function keepingPiece() {
      return !isOpen();
    }

    function looseRestock() {
      return keepingPiece() && !!answers.flexibility && answers.flexibility !== FLEX_EXACT;
    }

    function requestMode() {
      return keepingPiece() ? MODE_RESTOCK : MODE_OPEN;
    }

    var QUESTIONS = [
      { id: 'intro', type: 'intro' },

      {
        id: 'reference_piece',
        type: 'choice',
        card: true,
        required: true,
        review: 'The piece',
        title: "Is this the piece you're after?",
        when: hasPiece,
        options: [
          { v: REF_YES, l: REF_YES, hint: 'Find me this reference' },
          { v: REF_CLOSE, l: REF_CLOSE, hint: 'Use it as the starting point' },
          { v: REF_NO, l: REF_NO, hint: 'Describe something different' }
        ]
      },

      {
        id: 'flexibility',
        type: 'choice',
        required: true,
        review: 'How close',
        title: 'How close does it need to be?',
        note: SOURCING_LINE,
        when: keepingPiece,
        options: [
          { v: FLEX_EXACT, l: FLEX_EXACT, hint: 'Same configuration' },
          {
            v: 'Same model, details can vary',
            l: 'Same model, details can vary',
            hint: 'Dial, metal or bracelet can differ'
          },
          {
            v: 'Similar style, your pick',
            l: 'Similar style, your pick',
            hint: 'Bring me something in this spirit'
          }
        ]
      },

      {
        id: 'loved',
        type: 'multi',
        max: 3,
        required: true,
        review: 'Drew you to it',
        title: 'What drew you to it?',
        desc: 'Up to three. It tells us what to protect when we look.',
        when: looseRestock,
        options: LOVED
      },

      {
        id: 'must_match',
        type: 'multi',
        review: 'Must match',
        title: 'Anything that has to match?',
        desc: 'Leave it empty if you would rather see the options.',
        when: looseRestock,
        options: mustMatchOptions
      },

      {
        id: 'brand',
        type: 'choice',
        required: true,
        review: 'Brand',
        title: 'Which brand are you after?',
        writeIn: OTHER_BRAND,
        writeLabel: 'Which brand?',
        writePlaceholder: 'e.g. Breitling',
        when: isOpen,
        options: brandOptions
      },

      {
        id: 'style',
        type: 'choice',
        required: true,
        review: 'Style',
        title: 'What kind of watch?',
        when: function () {
          return isOpen() && answers.brand === ANY_BRAND;
        },
        options: [
          { v: 'Everyday classic', l: 'Everyday classic' },
          { v: 'Sport & tool', l: 'Sport & tool' },
          { v: 'Dress & formal', l: 'Dress & formal' },
          { v: 'Integrated bracelet', l: 'Integrated bracelet' }
        ]
      },

      {
        id: 'model',
        type: 'choice',
        required: true,
        review: 'Model',
        title: 'Which model?',
        writeIn: SOMETHING_ELSE,
        writeLabel: 'Which model?',
        writePlaceholder: 'Model or name',
        extra: {
          id: 'reference',
          label: 'Reference number, if you know it',
          placeholder: 'e.g. 1601',
          review: 'Reference'
        },
        when: function () {
          return isOpen() && !!answers.brand && answers.brand !== ANY_BRAND;
        },
        /* With nothing but the write-in on offer, pre-select it so the field is already open
           instead of asking for a tap that has only one possible answer. */
        prepare: function (q) {
          if (optionsFor(q).length === 1) answers.model = SOMETHING_ELSE;
        },
        options: modelOptions
      },

      {
        id: 'movement',
        type: 'choice',
        required: true,
        review: 'Movement',
        title: 'How should it run?',
        when: isOpen,
        options: [
          { v: 'Automatic', l: 'Automatic', hint: 'Self-winding' },
          { v: 'Manual wind', l: 'Manual wind', hint: 'Hand-wound' },
          { v: 'Quartz', l: 'Quartz', hint: 'Battery' },
          { v: 'No preference', l: 'No preference' }
        ]
      },

      {
        id: 'complications',
        type: 'multi',
        exclusive: JUST_TIME,
        review: 'Layout',
        title: 'What should the dial do?',
        desc: 'Only what we can realistically pair with your movement is listed.',
        when: isOpen,
        options: complicationOptions
      },

      {
        id: 'material',
        type: 'multi',
        review: 'Metal',
        title: 'Which metals work for you?',
        when: isOpen,
        options: function () {
          return scoped('materials');
        }
      },

      {
        id: 'case_size',
        /* Multi rather than single: wrist size rarely maps to one bracket, and a customer who
           will take 35-40mm or larger has twice the archive open to them. */
        type: 'multi',
        review: 'Case size',
        title: 'What size suits you?',
        when: isOpen,
        options: function () {
          return scoped('sizes');
        }
      },

      {
        id: 'must_haves',
        type: 'multi',
        exclusive: NOTHING_FIXED,
        review: 'Non-negotiable',
        title: 'Which of these are non-negotiable?',
        desc: 'Everything you leave unticked we will treat as a bonus, not a requirement.',
        when: function () {
          return isOpen() && pickedOptions().length > 1;
        },
        options: pickedOptions
      },

      {
        id: 'box_papers',
        type: 'choice',
        required: true,
        review: 'Box & papers',
        /* Worded exactly as the sell/trade page asks it, so a customer who has used both does
           not meet two spellings of the same question. */
        title: 'Box & Papers?',
        options: [
          { v: 'Need them', l: 'Need them', hint: 'Deal-breaker without' },
          { v: 'Want them', l: 'Want them', hint: 'Preferred, not essential' },
          { v: "Don't care", l: "Don't care" }
        ]
      },

      {
        id: 'budget',
        type: 'choice',
        /* An open inquiry without a budget is not actionable. A restock request has the sold
           price as its anchor already, so asking again is friction we do not need. */
        required: isOpen,
        review: 'Budget',
        title: "What's your budget?",
        writeIn: OWN_NUMBER,
        writeLabel: 'What are you working with?',
        writePlaceholder: 'e.g. up to $12,000',
        extra: {
          id: 'notes',
          type: 'long',
          label: 'Anything else we should know?',
          placeholder: 'Condition, timing, a piece you have seen elsewhere',
          review: 'Notes'
        },
        options: budgetOptions
      },

      {
        id: 'contact',
        type: 'contact',
        required: true,
        review: 'Contact',
        title: 'Your contact details',
        desc: 'So a specialist can come back to you directly.'
      },

      /* On its own screen rather than tucked under the summary. The summary is already the
         longest screen in the flow, and on a laptop it scrolls inside the card, which would put
         the one thing the customer has to read and agree to below the fold. */
      {
        id: 'disclosure',
        type: 'disclosure',
        required: true,
        title: 'Before we start looking',
        desc: 'Two things worth knowing about how sourcing works here.'
      },

      { id: 'review', type: 'review', title: 'Check your request' }
    ];

    function questionById(id) {
      for (var i = 0; i < QUESTIONS.length; i++) {
        if (QUESTIONS[i].id === id) return QUESTIONS[i];
      }
      return null;
    }

    function optionsFor(q) {
      var opts = typeof q.options === 'function' ? q.options() : q.options;
      return (opts || []).map(option);
    }

    /* Whether a question is required can depend on which entry path the customer is on. */
    function isRequired(q) {
      return typeof q.required === 'function' ? !!q.required() : !!q.required;
    }

    function isVisible(q) {
      if (!q) return false;
      if (q.id === 'intro' && introSkipped) return false;
      return !q.when || !!q.when();
    }

    /* -------------------------------------------------------------- persistence */

    function save() {
      try {
        sessionStorage.setItem(
          STORE_KEY,
          JSON.stringify({ answers: answers, piece: piece, reached: reached, at: Date.now() })
        );
      } catch (err) {
        /* private browsing or a full quota: persistence is a convenience, not a requirement */
      }
    }

    function clearSaved() {
      try {
        sessionStorage.removeItem(STORE_KEY);
      } catch (err) {
        /* ignore */
      }
    }

    var ANSWERED_KEYS = [
      'reference_piece',
      'flexibility',
      'loved',
      'must_match',
      'brand',
      'style',
      'model',
      'reference',
      'movement',
      'complications',
      'material',
      'case_size',
      'must_haves',
      'box_papers',
      'budget',
      'notes'
    ];

    function anythingAnswered() {
      for (var i = 0; i < ANSWERED_KEYS.length; i++) {
        if (asList(answers[ANSWERED_KEYS[i]]).length) return true;
      }
      return !!answers.contact;
    }

    /* Resuming only makes sense within the same entry path, and the saved piece is what says
       which path a session was on. A saved restock session reopened at the bare url is a
       different request, and carrying its answers over would show a sourcing inquiry pre-filled
       from a piece the customer is no longer looking at. */
    function restore(handle) {
      var raw;
      try {
        raw = sessionStorage.getItem(STORE_KEY);
      } catch (err) {
        return false;
      }
      if (!raw) return false;
      var data;
      try {
        data = JSON.parse(raw);
      } catch (err) {
        return false;
      }
      if (!data || !data.answers) return false;

      var savedHandle = data.piece ? data.piece.handle : '';
      if (savedHandle !== handle) {
        clearSaved();
        return false;
      }

      answers = data.answers;
      piece = data.piece || null;
      reached = data.reached || '';
      return anythingAnswered();
    }

    /* -------------------------------------------------------------- reference piece

       Two sources, in order of trust. The snippet on the product page renders the piece's own
       metafields straight into sessionStorage, which is the only way to see custom.model,
       custom.material, custom.case_size and custom.bracelet without a Storefront token. A link
       opened fresh (shared, bookmarked, new tab) has no such hand-off, so fall back to Shopify's
       own product JSON, which still yields the title, photo, price and the brand / shape / budget
       facet tags. Fewer "has to match" options in that case, nothing broken. */

    function shapeLabel(slug) {
      var labels = MANIFEST.shape_labels || {};
      return labels[slug] || '';
    }

    function readHandoff(handle) {
      var raw;
      try {
        raw = sessionStorage.getItem(REF_KEY);
      } catch (err) {
        return null;
      }
      if (!raw) return null;
      try {
        var data = JSON.parse(raw);
        if (!data || data.handle !== handle) return null;
        data.shape_label = data.shape_label || shapeLabel(data.shape);
        return data;
      } catch (err) {
        return null;
      }
    }

    function fromProductJson(handle) {
      return fetch('/products/' + encodeURIComponent(handle) + '.js', {
        headers: { Accept: 'application/json' }
      })
        .then(function (res) {
          if (!res.ok) throw new Error('not found');
          return res.json();
        })
        .then(function (p) {
          var tags = p.tags || [];
          function facet(prefix) {
            for (var i = 0; i < tags.length; i++) {
              if (tags[i].indexOf(prefix) === 0) return tags[i].slice(prefix.length);
            }
            return '';
          }
          var shape = facet('shape:');
          return {
            handle: p.handle,
            title: p.title || '',
            url: p.url || '/products/' + p.handle,
            image: p.featured_image || '',
            price: typeof p.price === 'number' ? money(p.price / 100) : '',
            brand: BRAND_SLUGS[facet('brand:')] || '',
            budget: facet('budget:'),
            shape: shape,
            shape_label: shapeLabel(shape)
          };
        });
    }

    function resolvePiece(handle) {
      var handoff = readHandoff(handle);
      if (handoff) return Promise.resolve(handoff);
      return fromProductJson(handle).catch(function () {
        return null;
      });
    }

    /* -------------------------------------------------------------- deep links

       ?ref=<product-handle>   the restock path: open on the piece
       (no parameters)         the open inquiry path */

    function refHandle() {
      var qs;
      try {
        qs = new URLSearchParams(location.search);
      } catch (err) {
        return '';
      }
      var ref = qs.get('ref') || '';
      return /^[a-z0-9][a-z0-9-]*$/i.test(ref) ? ref : '';
    }

    /* -------------------------------------------------------------- flow */

    function visibleQuestions() {
      var out = [];
      for (var i = 0; i < QUESTIONS.length; i++) {
        if (isVisible(QUESTIONS[i])) out.push(i);
      }
      return out;
    }

    function numberedIndexes() {
      return order.filter(function (i) {
        var t = QUESTIONS[i].type;
        return t !== 'intro' && t !== 'review';
      });
    }

    /* Every question whose options depend on an earlier answer, so a stale pick is possible. */
    var PRUNED = [
      'model',
      'complications',
      'material',
      'case_size',
      'must_match',
      'must_haves',
      'budget'
    ];

    /* Answers that came from options no longer on offer have to go, or a request can carry a
       quartz moonphase picked before the movement was changed, or a Datejust under Cartier.
       Anything hanging off a dropped answer goes with it: a reference number typed under one
       model does not describe another. Called before every validate and every payload. */
    function pruneAnswers() {
      var changed = false;
      PRUNED.forEach(function (id) {
        var q = questionById(id);
        if (!isVisible(q)) return;
        var allowed = optionsFor(q).map(function (o) {
          return o.v;
        });
        if (!allowed.length) return;

        if (q.type === 'multi') {
          var had = asList(answers[id]);
          var kept = had.filter(function (v) {
            return allowed.indexOf(v) > -1;
          });
          if (kept.length !== had.length) {
            answers[id] = kept;
            changed = true;
          }
          return;
        }

        if (answers[id] && allowed.indexOf(answers[id]) < 0) {
          delete answers[id];
          delete answers[id + '_note'];
          if (q.extra) delete answers[q.extra.id];
          changed = true;
        }
      });
      /* The saved session is what a reload resumes from, so it must not be left holding an answer
         the form has already ruled out. */
      if (changed) save();
    }

    /* What the customer answered, as one string: the choice plus its write-in, or a multi-select
       joined up. The backend and the summary screen both read this, so they cannot drift. */
    function answerText(q) {
      if (!q) return '';
      if (q.type === 'multi') return asList(answers[q.id]).join(', ');
      var v = String(answers[q.id] || '').trim();
      if (!v) return '';
      if (q.writeIn && v === q.writeIn) {
        var note = String(answers[q.id + '_note'] || '').trim();
        return note ? v + ': ' + note : v;
      }
      return v;
    }

    function textFor(id) {
      return answerText(questionById(id));
    }

    /* -------------------------------------------------------------- validation */

    function emailLooksValid(v) {
      return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v || '');
    }

    function phoneLooksValid(v) {
      return String(v || '').replace(/\D/g, '').length >= 10;
    }

    function contactErrors() {
      var c = answers.contact || {};
      var errs = {};
      if (!String(c.first || '').trim()) errs.first = 'Enter your first name.';
      if (!String(c.last || '').trim()) errs.last = 'Enter your last name.';
      if (!emailLooksValid(c.email)) errs.email = 'Enter a valid email, like name@email.com.';
      if (!phoneLooksValid(c.phone)) errs.phone = 'Enter a phone number with at least 10 digits.';
      return errs;
    }

    /* Returns true when the question passes, or a message explaining what is missing. */
    function validate(q) {
      if (q.type === 'disclosure') {
        return answers.ack ? true : 'Tick the box to continue.';
      }
      if (q.type === 'contact') {
        var errs = contactErrors();
        var keys = Object.keys(errs);
        return keys.length ? errs[keys[0]] : true;
      }
      if (q.type === 'choice' && q.writeIn && answers[q.id] === q.writeIn) {
        return String(answers[q.id + '_note'] || '').trim()
          ? true
          : 'Add a little more to continue.';
      }
      if (q.type === 'multi') {
        if (isRequired(q) && !asList(answers[q.id]).length) return 'Pick at least one to continue.';
        return true;
      }
      if (!isRequired(q)) return true;
      if (q.type === 'choice') return answers[q.id] ? true : 'Choose an option to continue.';
      if (q.type === 'short' || q.type === 'long') {
        return String(answers[q.id] || '').trim() ? true : 'This one is needed to continue.';
      }
      return true;
    }

    /* After a restore we cannot trust a saved position, because the branches a saved answer opens
       may put a question somewhere else in the order. Recompute it as the furthest question the
       customer actually reached, pulled back to anything before it that still needs an answer.
       Validation alone is not enough: most questions here are optional, so it would march a
       half-finished request past every question the customer had not got to yet. */
    function posOfId(id) {
      return id ? order.indexOf(QUESTIONS.indexOf(questionById(id))) : -1;
    }

    /* A high-water mark, kept as a question id rather than a position because the position of any
       one question moves as branches open and close. Going Back does not lower it: the customer
       has still seen what they have seen. */
    function markReached(q) {
      if (posOfId(q.id) <= posOfId(reached)) return;
      reached = q.id;
      save();
    }

    function firstIncompletePos() {
      var stop = order.length - 1;
      var at = posOfId(reached);
      if (at > -1) stop = at;
      for (var i = 0; i < stop; i++) {
        var q = QUESTIONS[order[i]];
        if (q.type === 'intro' || q.type === 'review') continue;
        if (validate(q) !== true) return i;
      }
      return Math.max(0, stop);
    }

    /* -------------------------------------------------------------- rendering */

    function activeScreen() {
      return stage.querySelector('[data-active]');
    }

    function swap(screen) {
      var old = activeScreen();
      if (old) {
        old.removeAttribute('data-active');
        old.setAttribute('data-leaving', '');
        setTimeout(function () {
          if (old.parentNode) old.parentNode.removeChild(old);
        }, 400);
      }
      stage.appendChild(screen);
      /* Forcing a reflow commits the pre-transition style, so the fade-in plays without
         requestAnimationFrame, which is throttled in background tabs. */
      void screen.offsetHeight;
      screen.setAttribute('data-active', '');
    }

    function syncChrome(q, index, total) {
      if (progressFill) {
        var pct = 2;
        if (q.type === 'review') pct = 100;
        else if (q.type !== 'intro' && total > 0) pct = Math.max(6, Math.round((index / total) * 100));
        progressFill.style.width = pct + '%';
      }
      if (q.type === 'intro') root.removeAttribute('data-started');
      else root.setAttribute('data-started', '');
    }

    function flowLabel() {
      return requestMode() === MODE_RESTOCK ? 'Restock request' : 'Sourcing request';
    }

    function money(amount) {
      var n = parseFloat(amount);
      if (!isFinite(n)) return '';
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: CURRENCY,
          maximumFractionDigits: 0
        }).format(n);
      } catch (err) {
        return '$' + Math.round(n).toLocaleString();
      }
    }

    function absoluteUrl(path) {
      try {
        return new URL(path, location.href).href;
      } catch (err) {
        return path;
      }
    }

    /* -------------------------------------------------------------- field builders */

    /* Letter shortcuts only make sense while every option still has a letter. Past that the
       badges would start counting in digits and the keyboard handler would advertise keys it
       does not answer to, so both go away together. */
    function hasKeys(q) {
      return optionsFor(q).length <= KEY_LETTERS.length;
    }

    /* How many columns the list asks for, from how long it is: a handful reads best one per row,
       a dozen will not fit the panel that way, and past a dozen even two columns overflow a
       laptop. The CSS decides whether the screen is wide enough to honour the request, so this
       is a ceiling rather than a layout. */
    function columnsFor(q) {
      var n = optionsFor(q).length;
      if (n > 12) return 3;
      if (n > 6) return 2;
      return 1;
    }

    function buildChoices(q, labelledBy) {
      var opts = optionsFor(q);
      var selected = answers[q.id];
      var keyed = hasKeys(q);
      var wrap = node(
        '<div class="dvw-st__choices" role="radiogroup" aria-labelledby="' + labelledBy + '"></div>'
      );
      var cols = columnsFor(q);
      if (cols > 1) wrap.setAttribute('data-cols', String(cols));
      if (!keyed) wrap.setAttribute('data-nokeys', '');

      opts.forEach(function (o, i) {
        var btn = node(
          '<button type="button" class="dvw-st__choice" role="radio" aria-checked="false">' +
            (keyed ? '<span class="dvw-st__key"></span>' : '') +
            '<span class="dvw-st__choicetext"></span>' +
            '<span class="dvw-st__tick">' +
            ICON_CHECK +
            '</span>' +
            '</button>'
        );
        btn.setAttribute('data-value', o.v);
        if (keyed) btn.querySelector('.dvw-st__key').textContent = KEY_LETTERS[i];

        var text = btn.querySelector('.dvw-st__choicetext');
        text.textContent = o.l;
        if (o.hint) {
          var hint = document.createElement('span');
          hint.className = 'dvw-st__dropsub';
          hint.textContent = o.hint;
          text.appendChild(hint);
        }

        if (selected === o.v) {
          btn.setAttribute('aria-checked', 'true');
          btn.setAttribute('tabindex', '0');
        } else {
          btn.setAttribute('tabindex', selected ? '-1' : i === 0 ? '0' : '-1');
        }
        wrap.appendChild(btn);
      });

      return wrap;
    }

    /* Multi-select renders as chips rather than stacked rows: these lists run to eight or nine
       options and a column of 54px rows would not fit the fixed panel. */
    function buildChips(q, labelledBy) {
      var opts = optionsFor(q);
      var chosen = asList(answers[q.id]);
      var wrap = node('<div class="dvw-rq__chipwrap"></div>');
      var group = node(
        '<div class="dvw-rq__chips" role="group" aria-labelledby="' + labelledBy + '"></div>'
      );

      opts.forEach(function (o) {
        var isExclusive = q.exclusive && o.v === q.exclusive;
        var chip = node(
          '<button type="button" class="dvw-rq__chip" aria-pressed="false">' +
            '<span class="dvw-rq__chipcheck">' +
            ICON_CHECK +
            '</span>' +
            '<span class="dvw-rq__chiptext"></span>' +
            '</button>'
        );
        chip.setAttribute('data-value', o.v);
        if (isExclusive) chip.setAttribute('data-exclusive', '');
        var text = chip.querySelector('.dvw-rq__chiptext');
        text.textContent = o.l;
        if (o.hint) {
          var hint = document.createElement('span');
          hint.className = 'dvw-rq__chiphint';
          hint.textContent = o.hint;
          text.appendChild(hint);
        }
        if (chosen.indexOf(o.v) > -1) chip.setAttribute('aria-pressed', 'true');
        group.appendChild(chip);
      });

      wrap.appendChild(group);
      wrap.appendChild(node('<p class="dvw-rq__chipcount" data-chipcount></p>'));
      return wrap;
    }

    function buildExtra(q) {
      if (!q.extra) return document.createDocumentFragment();
      var wrap = node('<div class="dvw-rq__extra"></div>');
      var id = 'dvw-rq-extra';
      var label = node('<label class="dvw-st__label"></label>');
      label.setAttribute('for', id);
      label.textContent = q.extra.label;
      wrap.appendChild(label);
      var input =
        q.extra.type === 'long'
          ? node('<textarea class="dvw-st__textarea" rows="2" data-extra></textarea>')
          : node('<input type="text" class="dvw-st__input" autocomplete="off" data-extra>');
      input.id = id;
      input.placeholder = q.extra.placeholder || '';
      input.value = answers[q.extra.id] || '';
      wrap.appendChild(input);
      return wrap;
    }

    function buildRefCard() {
      if (!piece) return document.createDocumentFragment();
      var card = node(
        '<div class="dvw-rq__piece">' +
          '<div class="dvw-rq__piecemedia"></div>' +
          '<div class="dvw-rq__piecebody">' +
          '<p class="dvw-rq__piecetitle"></p>' +
          '<p class="dvw-rq__piecemeta"></p>' +
          '</div>' +
          '</div>'
      );
      var media = card.querySelector('.dvw-rq__piecemedia');
      if (piece.image) {
        var img = node('<img class="dvw-rq__pieceimg" alt="" loading="eager">');
        img.src = piece.image;
        media.appendChild(img);
      } else {
        media.setAttribute('data-empty', '');
      }
      card.querySelector('.dvw-rq__piecetitle').textContent = piece.title || 'This listing';
      var meta = [piece.price, piece.brand].filter(Boolean).join(' · ');
      var metaEl = card.querySelector('.dvw-rq__piecemeta');
      if (meta) metaEl.textContent = meta;
      else metaEl.setAttribute('hidden', '');
      return card;
    }

    function buildContact() {
      var c = answers.contact || {};
      var grid = node('<div class="dvw-st__grid"></div>');

      /* span: 'full' takes the row, 'pair' shares it from tablet width up (an email address is
         too long for a half-width field on a phone). */
      function field(id, label, type, extra, span) {
        var wrap = node('<div class="dvw-st__field"></div>');
        if (span) wrap.classList.add('dvw-st__field--' + span);
        var lab = node('<label class="dvw-st__label"></label>');
        lab.setAttribute('for', id);
        lab.textContent = label;
        var inp = node('<input class="dvw-st__input">');
        inp.id = id;
        inp.type = type;
        inp.setAttribute('data-contact', extra.key);
        inp.setAttribute('autocomplete', extra.autocomplete);
        if (extra.inputMode) inp.inputMode = extra.inputMode;
        if (extra.placeholder) inp.placeholder = extra.placeholder;
        inp.value = c[extra.key] || '';
        var err = node('<p class="dvw-st__fielderr"></p>');
        err.id = id + '-error';
        inp.setAttribute('aria-describedby', err.id);
        wrap.appendChild(lab);
        wrap.appendChild(inp);
        wrap.appendChild(err);
        return wrap;
      }

      grid.appendChild(
        field('dvw-rq-first', 'First name', 'text', { key: 'first', autocomplete: 'given-name' })
      );
      grid.appendChild(
        field('dvw-rq-last', 'Last name', 'text', { key: 'last', autocomplete: 'family-name' })
      );
      grid.appendChild(
        field(
          'dvw-rq-email',
          'Email',
          'email',
          { key: 'email', autocomplete: 'email', inputMode: 'email' },
          'pair'
        )
      );
      grid.appendChild(
        field(
          'dvw-rq-phone',
          'Phone number',
          'tel',
          { key: 'phone', autocomplete: 'tel', inputMode: 'tel', placeholder: '(555) 123-4567' },
          'pair'
        )
      );

      var methodWrap = node('<div class="dvw-st__field dvw-st__field--full"></div>');
      methodWrap.appendChild(node('<span class="dvw-st__label">Best way to reach you</span>'));
      var methods = node('<div class="dvw-rq__methods" data-methods></div>');
      ['Email', 'Text', 'Phone call'].forEach(function (m) {
        var btn = node(
          '<button type="button" class="dvw-rq__method" aria-pressed="false"></button>'
        );
        btn.setAttribute('data-method', m);
        btn.textContent = m;
        if ((c.method || 'Email') === m) btn.setAttribute('aria-pressed', 'true');
        methods.appendChild(btn);
      });
      methodWrap.appendChild(methods);
      grid.appendChild(methodWrap);

      var consentWrap = node('<div class="dvw-st__field dvw-st__field--full"></div>');
      var consentLabel = node(
        '<label class="dvw-st__consent">' +
          '<input type="checkbox" class="dvw-st__checkbox" id="dvw-rq-consent">' +
          '<span></span>' +
          '</label>'
      );
      consentLabel.querySelector('span').textContent =
        "Email me when something matching this comes in, plus new arrivals. Unsubscribe anytime.";
      consentLabel.querySelector('input').checked = !!c.consent;
      consentWrap.appendChild(consentLabel);
      grid.appendChild(consentWrap);

      return grid;
    }

    function buildField(q, titleId) {
      if (q.type === 'choice') {
        var frag = document.createDocumentFragment();
        frag.appendChild(buildChoices(q, titleId));
        if (q.writeIn) {
          var wrap = node('<div class="dvw-st__writein" data-writein-wrap hidden></div>');
          var label = node('<label class="dvw-st__label" for="dvw-rq-writein"></label>');
          label.textContent = q.writeLabel || 'Please describe';
          var write = node(
            '<input type="text" class="dvw-st__input" id="dvw-rq-writein" autocomplete="off" data-writein>'
          );
          write.placeholder = q.writePlaceholder || '';
          write.value = answers[q.id + '_note'] || '';
          wrap.appendChild(label);
          wrap.appendChild(write);
          if (answers[q.id] === q.writeIn) wrap.removeAttribute('hidden');
          frag.appendChild(wrap);
        }
        frag.appendChild(buildExtra(q));
        return frag;
      }

      if (q.type === 'multi') return buildChips(q, titleId);

      if (q.type === 'long') {
        var ta = node('<textarea class="dvw-st__textarea" rows="4" data-field></textarea>');
        ta.setAttribute('aria-labelledby', titleId);
        ta.placeholder = q.placeholder || '';
        ta.value = answers[q.id] || '';
        return ta;
      }

      if (q.type === 'short') {
        var input = node('<input type="text" class="dvw-st__input" autocomplete="off" data-field>');
        input.setAttribute('aria-labelledby', titleId);
        input.placeholder = q.placeholder || '';
        input.value = answers[q.id] || '';
        return input;
      }

      if (q.type === 'contact') return buildContact();

      if (q.type === 'disclosure') return buildDisclosure();

      return document.createDocumentFragment();
    }

    /* -------------------------------------------------------------- review step */

    function reviewRows() {
      var rows = [];
      order.forEach(function (qi) {
        var q = QUESTIONS[qi];
        if (q.type === 'intro' || q.type === 'review' || q.type === 'disclosure') return;

        if (q.type === 'contact') {
          var c = answers.contact || {};
          var lines = [
            (String(c.first || '') + ' ' + String(c.last || '')).trim(),
            c.email || '',
            c.phone || '',
            c.method ? 'Prefers ' + c.method.toLowerCase() : ''
          ].filter(Boolean);
          rows.push({ label: q.review || 'Contact', value: lines.join('\n'), target: q.id });
          return;
        }

        if (q.id === 'reference_piece') {
          /* The URL still goes to the backend; printing it here cost four wrapped lines. */
          var value = answerText(q);
          if (keepingPiece()) value = (piece.title || 'Listing') + '\nListing attached';
          rows.push({ label: q.review, value: value, target: q.id });
          return;
        }

        rows.push({ label: q.review || q.title, value: answerText(q), target: q.id });
        if (q.extra) {
          var extraValue = String(answers[q.extra.id] || '').trim();
          if (extraValue) {
            rows.push({ label: q.extra.review || q.extra.label, value: extraValue, target: q.id });
          }
        }
      });
      return rows;
    }

    function buildReview() {
      var wrap = node('<div class="dvw-st__review"></div>');
      reviewRows().forEach(function (row) {
        var el = node(
          '<div class="dvw-st__reviewrow">' +
            '<p class="dvw-st__reviewkey"></p>' +
            '<p class="dvw-st__reviewval"></p>' +
            '<button type="button" class="dvw-st__btn dvw-st__btn--link">Edit</button>' +
            '</div>'
        );
        el.querySelector('.dvw-st__reviewkey').textContent = row.label;
        var val = el.querySelector('.dvw-st__reviewval');
        if (row.value) {
          val.textContent = row.value;
        } else {
          val.textContent = 'Not provided';
          val.setAttribute('data-empty', '');
        }
        el.querySelector('button').addEventListener('click', function () {
          var idx = order.indexOf(QUESTIONS.indexOf(questionById(row.target)));
          if (idx > -1) {
            pos = idx;
            pushHistory();
            render();
          }
        });
        wrap.appendChild(el);
      });
      return wrap;
    }

    /* Both disclosures sit behind one acknowledgement. The deposit terms are the store's
       published wording, quoted rather than paraphrased. */
    function buildDisclosure() {
      var wrap = node('<div class="dvw-rq__disclose"></div>');
      /* The terms sit in their own box so that on a phone, where the same words run to twice the
         lines, it is the reading that scrolls and not the screen: the tick and the button below
         it stay where the customer can see them. */
      var body = node('<div class="dvw-rq__disclosebody"></div>');

      var sourcing = node('<p class="dvw-rq__disclosetext"></p>');
      sourcing.textContent =
        'Every watch we handle is vintage, with character that cannot be replicated, and some ' +
        'configurations are harder to find than others. We will work with you to find an example ' +
        'of the reference you want, carrying as many of your desired attributes as we can, at the ' +
        'level of quality you are after. Where a piece has already sold, we will do our best to ' +
        'find one in similar or better condition. Final price can vary with configuration and ' +
        'condition.';
      body.appendChild(sourcing);

      var deposit = node('<p class="dvw-rq__disclosetext"></p>');
      deposit.textContent =
        'Deposits are accepted for intended purchases only and are not intended to hold a watch ' +
        'while a customer considers a potential purchase. We accept a non-refundable deposit of ' +
        '10% toward the purchase for us to hold the watch. No deposit is taken by this form.';
      body.appendChild(deposit);

      var link = node('<p class="dvw-rq__disclosetext"></p>');
      var anchor = document.createElement('a');
      anchor.className = 'dvw-rq__discloselink';
      anchor.href = POLICY_URL;
      anchor.textContent = 'Read the full policies';
      link.appendChild(anchor);
      body.appendChild(link);
      wrap.appendChild(body);

      var ackWrap = node('<div class="dvw-rq__ack"></div>');
      var ack = node(
        '<label class="dvw-st__consent">' +
          '<input type="checkbox" class="dvw-st__checkbox" id="dvw-rq-ack" data-ack>' +
          '<span></span>' +
          '</label>'
      );
      ack.querySelector('span').textContent = 'I understand how sourcing and deposits work.';
      ack.querySelector('input').checked = !!answers.ack;
      ackWrap.appendChild(ack);
      wrap.appendChild(ackWrap);

      return wrap;
    }

    /* -------------------------------------------------------------- screens */

    function render() {
      pruneAnswers();
      order = visibleQuestions();
      if (pos >= order.length) pos = order.length - 1;
      if (pos < 0) pos = 0;

      var qi = order[pos];
      var q = QUESTIONS[qi];
      var nums = numberedIndexes();
      var index = nums.indexOf(qi) + 1;
      var titleId = 'dvw-rq-title';

      markReached(q);

      syncChrome(q, index, nums.length);

      var screen;
      if (q.type === 'intro') screen = renderIntro(titleId);
      else if (q.type === 'review') screen = renderReview(titleId);
      else screen = renderQuestion(q, titleId, index, nums.length);

      swap(screen);
      focusFirst(screen, q);
      scrollIntoPlace();
    }

    function renderIntro(titleId) {
      var screen = node('<div class="dvw-st__screen"></div>');

      var title = node('<h1 class="dvw-st__title dvw-st__title--lg"></h1>');
      title.id = titleId;
      title.textContent = CFG.introTitle || 'Source a Watch';
      screen.appendChild(title);

      if (CFG.introText) {
        var desc = node('<p class="dvw-st__desc"></p>');
        desc.textContent = CFG.introText;
        screen.appendChild(desc);
      }

      var actions = node('<div class="dvw-st__actions"></div>');
      /* data-next marks the primary action on every screen; the keyboard handler uses its
         presence to tell an interactive screen from a loading or thank-you one. */
      var start = node(
        '<button type="button" class="dvw-st__btn" data-next>Get started ' + ICON_NEXT + '</button>'
      );
      start.addEventListener('click', function () {
        goTo(pos + 1);
      });
      actions.appendChild(start);
      actions.appendChild(node('<span class="dvw-st__hint">or press <kbd>Enter</kbd></span>'));
      screen.appendChild(actions);

      return screen;
    }

    function renderQuestion(q, titleId, index, total) {
      if (q.prepare) q.prepare(q);
      var screen = node('<div class="dvw-st__screen"></div>');

      var meta = node(
        '<p class="dvw-st__flow"><span data-flow></span><span class="dvw-st__count" data-count></span></p>'
      );
      meta.querySelector('[data-flow]').textContent = flowLabel();
      meta.querySelector('[data-count]').textContent =
        total > 0 ? 'Step ' + index + ' of ' + total : '';
      screen.appendChild(meta);

      if (q.card) screen.appendChild(buildRefCard());

      var title = node('<h2 class="dvw-st__title"></h2>');
      title.id = titleId;
      title.textContent = q.title;
      /* The marker means "we need an answer here". The disclosure needs an acknowledgement, not
         an answer, and its own checkbox says so. */
      if (isRequired(q) && q.type !== 'disclosure') {
        title.appendChild(node('<span class="dvw-st__req" aria-hidden="true"> *</span>'));
      }
      screen.appendChild(title);

      if (q.desc) {
        var desc = node('<p class="dvw-st__desc"></p>');
        desc.textContent = q.desc;
        screen.appendChild(desc);
      }

      screen.appendChild(buildField(q, titleId));

      if (q.note) {
        var note = node('<p class="dvw-rq__note"></p>');
        note.textContent = q.note;
        screen.appendChild(note);
      }

      screen.appendChild(node('<p class="dvw-st__error" role="alert" data-error></p>'));

      var actions = node('<div class="dvw-st__actions"></div>');
      if (pos > 0) {
        var back = node(
          '<button type="button" class="dvw-st__btn dvw-st__btn--ghost">' + ICON_BACK + 'Back</button>'
        );
        back.addEventListener('click', function () {
          collect(q, screen);
          save();
          goTo(pos - 1);
        });
        actions.appendChild(back);
      }
      actions.appendChild(node('<span class="dvw-st__spacer"></span>'));

      if (!isRequired(q)) {
        var skip = node('<button type="button" class="dvw-st__btn dvw-st__btn--link">Skip</button>');
        skip.addEventListener('click', function () {
          collect(q, screen);
          save();
          goTo(pos + 1);
        });
        actions.appendChild(skip);
      }

      var isLast = pos === order.length - 1;
      var next = node(
        '<button type="button" class="dvw-st__btn" data-next>' +
          (isLast ? 'Submit' : 'Continue') +
          ' ' +
          ICON_NEXT +
          '</button>'
      );
      next.addEventListener('click', function () {
        advance(q, screen);
      });
      actions.appendChild(next);
      screen.appendChild(actions);

      wireQuestion(q, screen);
      return screen;
    }

    function renderReview(titleId) {
      var screen = node('<div class="dvw-st__screen dvw-st__screen--review"></div>');

      var title = node('<h2 class="dvw-st__title"></h2>');
      title.id = titleId;
      title.textContent = 'Check your request';
      screen.appendChild(title);

      var desc = node('<p class="dvw-st__desc"></p>');
      desc.textContent = 'Edit anything that needs a change, then send it over.';
      screen.appendChild(desc);

      screen.appendChild(buildReview());
      screen.appendChild(node('<p class="dvw-st__error" role="alert" data-error></p>'));

      var actions = node('<div class="dvw-st__actions"></div>');
      var back = node(
        '<button type="button" class="dvw-st__btn dvw-st__btn--ghost">' + ICON_BACK + 'Back</button>'
      );
      back.addEventListener('click', function () {
        goTo(pos - 1);
      });
      actions.appendChild(back);
      actions.appendChild(node('<span class="dvw-st__spacer"></span>'));

      var send = node(
        '<button type="button" class="dvw-st__btn" data-next>Send my request ' + ICON_NEXT + '</button>'
      );
      send.addEventListener('click', function () {
        submit();
      });
      actions.appendChild(send);
      screen.appendChild(actions);

      return screen;
    }

    function focusFirst(screen, q) {
      if (pos === 0 && q.type === 'intro') return;
      var target =
        screen.querySelector('[data-field]') ||
        screen.querySelector('[data-contact]') ||
        screen.querySelector('[data-ack]') ||
        screen.querySelector('[aria-checked="true"]') ||
        screen.querySelector('.dvw-st__choice') ||
        screen.querySelector('.dvw-rq__chip');
      if (!target) return;
      setTimeout(function () {
        try {
          target.focus({ preventScroll: true });
        } catch (err) {
          /* older Safari ignores the options bag */
        }
      }, 380);
    }

    /* The panel is sized to the screen below whatever the theme puts above it. The stylesheet's
       fallback adds up the announcement bar and the header, which is right to within a pixel or
       two, but a header border or a different page template can throw that off, so measure it. */
    function fitToScreen() {
      var top = root.getBoundingClientRect().top + (window.scrollY || 0);
      root.style.setProperty('--dvw-st-chrome', Math.max(0, Math.round(top)) + 'px');
    }

    function scrollIntoPlace() {
      var rect = root.getBoundingClientRect();
      if (rect.top >= 0) return;
      var styles = getComputedStyle(document.documentElement);
      var offset = parseInt(styles.getPropertyValue('--header-height'), 10) || 0;
      window.scrollTo({ top: window.scrollY + rect.top - offset, behavior: 'smooth' });
    }

    /* -------------------------------------------------------------- wiring */

    function updateChipCount(q, screen) {
      var el = screen.querySelector('[data-chipcount]');
      if (!el) return;
      var chosen = asList(answers[q.id]);
      if (!chosen.length) {
        el.textContent = q.max ? 'Pick up to ' + q.max : '';
        return;
      }
      el.textContent = q.max
        ? chosen.length + ' of ' + q.max + ' chosen'
        : chosen.length + ' selected';
    }

    function wireChips(q, screen) {
      var chips = Array.prototype.slice.call(screen.querySelectorAll('.dvw-rq__chip'));

      function paint() {
        var chosen = asList(answers[q.id]);
        var atMax = !!q.max && chosen.length >= q.max;
        chips.forEach(function (chip) {
          var v = chip.getAttribute('data-value');
          var on = chosen.indexOf(v) > -1;
          chip.setAttribute('aria-pressed', on ? 'true' : 'false');
          /* At the cap, the un-chosen options go quiet rather than silently doing nothing. */
          if (atMax && !on) chip.setAttribute('data-capped', '');
          else chip.removeAttribute('data-capped');
        });
        updateChipCount(q, screen);
      }

      function toggle(chip) {
        var v = chip.getAttribute('data-value');
        var chosen = asList(answers[q.id]);
        var at = chosen.indexOf(v);
        var isExclusive = q.exclusive && v === q.exclusive;

        if (at > -1) chosen.splice(at, 1);
        else if (isExclusive) chosen = [v];
        else {
          chosen = chosen.filter(function (x) {
            return x !== q.exclusive;
          });
          if (q.max && chosen.length >= q.max) return;
          chosen.push(v);
        }

        answers[q.id] = chosen;
        save();
        hideError(screen);
        paint();
      }

      chips.forEach(function (chip, i) {
        chip.addEventListener('click', function () {
          toggle(chip);
        });
        chip.addEventListener('keydown', function (event) {
          var step =
            event.key === 'ArrowRight' || event.key === 'ArrowDown'
              ? 1
              : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
                ? -1
                : 0;
          if (!step) return;
          event.preventDefault();
          chips[(i + step + chips.length) % chips.length].focus();
        });
      });

      paint();
    }

    function wireChoiceGroup(group, onPick) {
      if (!group) return;
      var choices = Array.prototype.slice.call(group.querySelectorAll('.dvw-st__choice'));

      function select(el) {
        choices.forEach(function (c) {
          c.setAttribute('aria-checked', 'false');
          c.setAttribute('tabindex', '-1');
        });
        el.setAttribute('aria-checked', 'true');
        el.setAttribute('tabindex', '0');
        onPick(el.getAttribute('data-value'));
      }

      choices.forEach(function (el, i) {
        el.addEventListener('click', function () {
          select(el);
        });
        el.addEventListener('keydown', function (event) {
          var key = event.key;
          if (key === ' ' || key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            select(el);
            return;
          }
          var step =
            key === 'ArrowDown' || key === 'ArrowRight'
              ? 1
              : key === 'ArrowUp' || key === 'ArrowLeft'
                ? -1
                : 0;
          if (!step) return;
          event.preventDefault();
          var nextEl = choices[(i + step + choices.length) % choices.length];
          nextEl.setAttribute('tabindex', '0');
          el.setAttribute('tabindex', '-1');
          nextEl.focus();
        });
      });
    }

    function wireQuestion(q, screen) {
      if (q.type === 'multi') wireChips(q, screen);

      if (q.type === 'disclosure') {
        var ackInput = screen.querySelector('[data-ack]');
        if (ackInput) {
          ackInput.addEventListener('change', function () {
            answers.ack = !!ackInput.checked;
            save();
            if (answers.ack) hideError(screen);
          });
        }
      }

      if (q.extra) {
        var extraInput = screen.querySelector('[data-extra]');
        if (extraInput) {
          extraInput.addEventListener('input', function () {
            answers[q.extra.id] = extraInput.value;
          });
          extraInput.addEventListener('blur', function () {
            collect(q, screen);
            save();
          });
        }
      }

      if (q.type === 'choice') {
        var writeWrap = screen.querySelector('[data-writein-wrap]');
        var writeInput = screen.querySelector('[data-writein]');
        if (writeInput) {
          writeInput.addEventListener('input', function () {
            answers[q.id + '_note'] = writeInput.value;
            hideError(screen);
          });
          writeInput.addEventListener('blur', function () {
            collect(q, screen);
            save();
          });
        }

        wireChoiceGroup(screen.querySelector('.dvw-st__choices'), function (v) {
          answers[q.id] = v;
          save();

          if (q.writeIn && v === q.writeIn) {
            if (writeWrap) writeWrap.removeAttribute('hidden');
            hideError(screen);
            if (writeInput) {
              try {
                writeInput.focus({ preventScroll: true });
              } catch (err) {
                writeInput.focus();
              }
            }
            return;
          }
          if (writeWrap) writeWrap.setAttribute('hidden', '');

          /* A question with an optional field under the choices must not run away before the
             customer has had the chance to fill it in. */
          if (q.extra) return;

          /* Picking a branch answer changes which questions follow, so settle then advance. */
          setTimeout(function () {
            advance(q, screen);
          }, 200);
        });
      }

      if (q.type === 'contact') {
        screen.querySelectorAll('[data-contact]').forEach(function (input) {
          input.addEventListener('blur', function () {
            collect(q, screen);
            save();
            showFieldErrors(screen, true);
          });
          input.addEventListener('input', function () {
            var field = input.closest('.dvw-st__field');
            if (field && field.hasAttribute('data-invalid')) {
              field.removeAttribute('data-invalid');
              input.setAttribute('aria-invalid', 'false');
            }
            hideError(screen);
          });
        });

        var methods = Array.prototype.slice.call(screen.querySelectorAll('[data-method]'));
        methods.forEach(function (btn) {
          btn.addEventListener('click', function () {
            methods.forEach(function (b) {
              b.setAttribute('aria-pressed', 'false');
            });
            btn.setAttribute('aria-pressed', 'true');
            var c = answers.contact || {};
            c.method = btn.getAttribute('data-method');
            answers.contact = c;
            save();
          });
        });

        var consent = screen.querySelector('#dvw-rq-consent');
        if (consent) {
          consent.addEventListener('change', function () {
            collect(q, screen);
            save();
          });
        }
      }

      if (q.type === 'short' || q.type === 'long') {
        var field = screen.querySelector('[data-field]');
        if (field) {
          field.addEventListener('input', function () {
            answers[q.id] = field.value;
            hideError(screen);
          });
          field.addEventListener('blur', save);
        }
      }
    }

    function collect(q, screen) {
      screen = screen || activeScreen();
      if (!screen) return;

      if (q.extra) {
        var extra = screen.querySelector('[data-extra]');
        if (extra) answers[q.extra.id] = String(extra.value || '').trim();
      }

      if (q.type === 'short' || q.type === 'long') {
        var field = screen.querySelector('[data-field]');
        if (field) answers[q.id] = String(field.value || '').trim();
        return;
      }

      if (q.type === 'choice' && q.writeIn) {
        var write = screen.querySelector('[data-writein]');
        if (write) answers[q.id + '_note'] = String(write.value || '').trim();
        return;
      }

      if (q.type === 'contact') {
        var c = answers.contact || {};
        screen.querySelectorAll('[data-contact]').forEach(function (input) {
          c[input.getAttribute('data-contact')] = String(input.value || '').trim();
        });
        var pressed = screen.querySelector('[data-method][aria-pressed="true"]');
        c.method = pressed ? pressed.getAttribute('data-method') : c.method || 'Email';
        var consent = screen.querySelector('#dvw-rq-consent');
        c.consent = consent ? !!consent.checked : !!c.consent;
        answers.contact = c;
        answers.consent = c.consent;
      }
    }

    function showFieldErrors(screen, onlyTouched) {
      var errs = contactErrors();
      screen.querySelectorAll('[data-contact]').forEach(function (input) {
        var key = input.getAttribute('data-contact');
        var field = input.closest('.dvw-st__field');
        var errEl = field ? field.querySelector('.dvw-st__fielderr') : null;
        var hasValue = String(input.value || '').trim().length > 0;
        if (errs[key] && (!onlyTouched || hasValue || document.activeElement !== input)) {
          if (field) field.setAttribute('data-invalid', '');
          if (errEl) errEl.textContent = errs[key];
          input.setAttribute('aria-invalid', 'true');
        } else {
          if (field) field.removeAttribute('data-invalid');
          input.setAttribute('aria-invalid', 'false');
        }
      });
      return Object.keys(errs).length === 0;
    }

    function showError(screen, message) {
      var el = (screen || activeScreen() || root).querySelector('[data-error]');
      if (!el) return;
      el.textContent = message;
      el.setAttribute('data-shown', '');
    }

    function hideError(screen) {
      var el = (screen || activeScreen() || root).querySelector('[data-error]');
      if (!el) return;
      el.textContent = '';
      el.removeAttribute('data-shown');
    }

    /* -------------------------------------------------------------- navigation */

    function pushHistory() {
      if (!historyReady) return;
      try {
        history.pushState({ dvwPos: pos }, '');
      } catch (err) {
        /* history is a nicety; navigation still works without it */
      }
    }

    function goTo(next) {
      pos = Math.max(0, Math.min(next, order.length - 1));
      pushHistory();
      render();
    }

    function advance(q, screen) {
      collect(q, screen);
      save();

      if (q.type === 'contact' && !showFieldErrors(screen, false)) {
        showError(screen, 'Check the highlighted fields to continue.');
        return;
      }

      var ok = validate(q);
      if (ok !== true) {
        showError(screen, ok);
        return;
      }

      if (q.id === 'contact') sendPartialLead();

      pruneAnswers();
      order = visibleQuestions();
      if (pos >= order.length - 1) {
        submit();
        return;
      }
      goTo(pos + 1);
    }

    /* -------------------------------------------------------------- submit */

    /* A one-paragraph rollup of the whole request. The Worker, the Telegram alert and the leads
       dashboard have all read request_notes since the original form, so it keeps carrying the
       human-readable summary even though every answer now also ships as its own key. */
    function requestNotes() {
      var parts = [];
      function add(label, value) {
        var text = Array.isArray(value) ? value.join(', ') : String(value || '').trim();
        if (text) parts.push(label + ': ' + text);
      }
      add('Mode', requestMode());
      if (keepingPiece()) add('Reference piece', piece.title);
      add('How close', textFor('flexibility'));
      add('Drew them in', answers.loved);
      add('Must match', answers.must_match);
      add('Brand', textFor('brand'));
      add('Style', answers.style);
      add('Model', textFor('model'));
      add('Reference', answers.reference);
      add('Movement', answers.movement);
      add('Layout', answers.complications);
      add('Metal', answers.material);
      add('Case size', answers.case_size);
      add('Non-negotiable', answers.must_haves);
      add('Box & papers', answers.box_papers);
      add('Budget', textFor('budget'));
      add('Notes', answers.notes);
      var c = answers.contact || {};
      add('Prefers', c.method);
      if (answers.ack) add('Acknowledged', 'Sourcing and deposit terms');
      return parts.join('\n');
    }

    /* Only what this form actually asked. The backend keys off form_type and needs the contact
       block, so those always ship; every answer key is sent only once it has a value, so a
       question the customer never saw stays out of the alert and the Klaviyo profile entirely. */
    function payload(partial) {
      pruneAnswers();
      var out = {
        partial: partial,
        form_type: REQUEST,
        request_mode: requestMode(),
        consent: !!answers.consent,
        acknowledged: !!answers.ack,
        contact: answers.contact || {},
        submitted_at: new Date().toISOString(),
        page: location.href
      };

      if (keepingPiece()) {
        out.request_watch_url = absoluteUrl(piece.url || '/products/' + piece.handle);
        out.request_watch = piece.title || '';
      }

      ANSWERED_KEYS.forEach(function (id) {
        var q = questionById(id);
        /* A question the customer cannot currently see must not report an answer: going back and
           switching entry path or brand retires whole branches, and a stale answer from a retired
           branch would read as fact in the alert. reference and notes hang off another question,
           so they have no question of their own to check. */
        if (q && !isVisible(q)) return;
        var value = q ? answerText(q) : String(answers[id] || '').trim();
        if (value) out[id] = value;
      });

      var notes = requestNotes();
      if (notes) out.request_notes = notes;
      return out;
    }

    /* Fires once contact details are known so the team can follow up on abandoned requests. */
    function sendPartialLead() {
      if (partialSent || PREVIEW) return;
      var c = answers.contact || {};
      if (!c.email || !c.first) return;
      partialSent = true;
      try {
        fetch(SUBMIT_URL, {
          method: 'POST',
          mode: 'cors',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload(true)),
          keepalive: true
        }).catch(function () {});
      } catch (err) {
        /* never block the form on the partial capture */
      }
    }

    function submit() {
      if (submitting) return;

      if (!answers.ack) {
        showError(activeScreen(), 'Tick the box above to send your request.');
        return;
      }

      submitting = true;
      showLoading();

      if (PREVIEW) {
        setTimeout(function () {
          submitting = false;
          showDone(null);
        }, 900);
        return;
      }

      fetch(SUBMIT_URL, {
        method: 'POST',
        mode: 'cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload(false))
      })
        .then(function (res) {
          return res.json().catch(function () {
            return { ok: res.ok };
          });
        })
        .then(function (res) {
          submitting = false;
          if (res && res.ok) {
            clearSaved();
            showDone(res.id || null);
          } else {
            showFailure(res && res.error);
          }
        })
        .catch(function () {
          submitting = false;
          showFailure(null);
        });
    }

    function showSpinner(title, text) {
      var screen = node(
        '<div class="dvw-st__screen dvw-st__screen--center">' +
          '<div class="dvw-st__spinner"></div>' +
          '<h2 class="dvw-st__title"></h2>' +
          '<p class="dvw-st__desc"></p>' +
          '</div>'
      );
      screen.querySelector('.dvw-st__title').textContent = title;
      screen.querySelector('.dvw-st__desc').textContent = text;
      swap(screen);
      scrollIntoPlace();
    }

    function showLoading() {
      if (progressFill) progressFill.style.width = '100%';
      showSpinner('Sending your request', 'One moment.');
    }

    function showDone(reference) {
      var c = answers.contact || {};
      var screen = node(
        '<div class="dvw-st__screen dvw-st__screen--center">' +
          '<div class="dvw-st__tick-badge">' +
          ICON_CHECK +
          '</div>' +
          '<h1 class="dvw-st__title dvw-st__title--lg"></h1>' +
          '<p class="dvw-st__desc"></p>' +
          '<div class="dvw-st__actions"></div>' +
          '</div>'
      );

      screen.querySelector('.dvw-st__title').textContent = c.first
        ? 'Thank you, ' + c.first
        : 'Request received';
      screen.querySelector('.dvw-st__desc').textContent =
        'A specialist will start looking and come back to you' +
        (c.method ? ' by ' + c.method.toLowerCase() : '') +
        '. ' +
        SOURCING_LINE;

      var actions = screen.querySelector('.dvw-st__actions');
      var browse = document.createElement('a');
      browse.className = 'dvw-st__btn';
      browse.href = SHOP_ALL;
      browse.textContent = 'Browse the collection';
      actions.appendChild(browse);

      var home = document.createElement('a');
      home.className = 'dvw-st__btn dvw-st__btn--ghost';
      home.href = HOME;
      home.textContent = 'Back to home';
      actions.appendChild(home);

      if (reference) {
        var ref = node('<p class="dvw-st__ref"></p>');
        ref.textContent = 'Reference ' + reference;
        screen.appendChild(ref);
      }

      swap(screen);
      scrollIntoPlace();
    }

    function showFailure(message) {
      var screen = node(
        '<div class="dvw-st__screen dvw-st__screen--center">' +
          '<h2 class="dvw-st__title">That didn\'t go through</h2>' +
          '<p class="dvw-st__desc"></p>' +
          '<div class="dvw-st__actions"></div>' +
          '</div>'
      );
      screen.querySelector('.dvw-st__desc').textContent =
        message ||
        'Check your connection and try again. Your answers are still here' +
          (PHONE ? ', or text us at ' + PHONE + '.' : '.');

      var retry = node('<button type="button" class="dvw-st__btn">Try again</button>');
      retry.addEventListener('click', function () {
        submit();
      });
      screen.querySelector('.dvw-st__actions').appendChild(retry);

      var back = node(
        '<button type="button" class="dvw-st__btn dvw-st__btn--ghost">Back to my request</button>'
      );
      back.addEventListener('click', function () {
        order = visibleQuestions();
        pos = order.length - 1;
        render();
      });
      screen.querySelector('.dvw-st__actions').appendChild(back);

      swap(screen);
      scrollIntoPlace();
    }

    /* -------------------------------------------------------------- keyboard

       Scoped to this section: the listener bails out when focus sits anywhere else on the page,
       so the theme's header search and cart drawer keep their own keys. */

    document.addEventListener('keydown', function (event) {
      var target = event.target;
      if (target !== document.body && !root.contains(target)) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      var screen = activeScreen();
      if (!screen || !screen.querySelector('[data-next]')) return;

      var qi = order[pos];
      if (qi === undefined) return;
      var q = QUESTIONS[qi];
      var tag = (target.tagName || '').toLowerCase();
      var typing = tag === 'input' || tag === 'textarea' || tag === 'select';

      if (q.type === 'choice' && !typing && hasKeys(q)) {
        var idx = KEY_LETTERS.indexOf((event.key || '').toUpperCase());
        if (idx > -1 && idx < optionsFor(q).length) {
          event.preventDefault();
          var btn = screen.querySelectorAll('.dvw-st__choice')[idx];
          if (btn) btn.click();
          return;
        }
      }

      if (event.key !== 'Enter') return;
      if (tag === 'textarea') return;
      if (tag === 'button' || tag === 'a') return;
      if (target === document.body && q.type !== 'intro') return;

      event.preventDefault();
      if (q.type === 'intro') goTo(pos + 1);
      else if (q.type === 'review') submit();
      else advance(q, screen);
    });

    window.addEventListener('popstate', function (event) {
      var state = event.state;
      var next = state && typeof state.dvwPos === 'number' ? state.dvwPos : 0;
      order = visibleQuestions();
      pos = Math.max(0, Math.min(next, order.length - 1));
      render();
    });

    /* -------------------------------------------------------------- init */

    function start() {
      order = visibleQuestions();
      pos = answers.__restored ? firstIncompletePos() : 0;
      delete answers.__restored;

      try {
        history.replaceState({ dvwPos: pos }, '');
      } catch (err) {
        /* ignore */
      }
      historyReady = true;

      render();
    }

    var noscript = root.querySelector('noscript');
    if (noscript) noscript.remove();

    fitToScreen();
    /* Re-measured on load because webfonts can still change the header's height after
       DOMContentLoaded, and on resize for rotation and window changes. */
    window.addEventListener('load', fitToScreen);
    window.addEventListener('resize', fitToScreen);

    var handle = refHandle();
    if (restore(handle)) answers.__restored = true;

    if (!handle) {
      start();
      return;
    }

    /* The piece has to be in hand before the first screen draws, because the first screen is the
       piece. Arriving from a listing also means the customer has already declared their intent,
       so the intro would be a tap for nothing. A restored session already has the piece;
       otherwise resolve it behind a spinner. */
    introSkipped = true;
    if (piece && piece.handle === handle) {
      start();
      return;
    }

    showSpinner('One moment', 'Loading that listing.');
    resolvePiece(handle).then(function (resolved) {
      piece = resolved;
      /* Nothing to confirm, so fall back to the flow that describes a piece from scratch,
         intro and all, since the customer never saw a starting point. */
      if (!piece) introSkipped = false;
      save();
      start();
    });
  }

  function init() {
    document.querySelectorAll('[data-dvw-request]').forEach(boot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor re-renders the section on every setting change. */
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-dvw-request]');
    if (root) boot(root);
  });
})();
